import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { InventorySummary } from '../../types';

interface ZoneSummaryProps {
  byZone: InventorySummary['by_zone'];
  isLoading?: boolean;
}

export function ZoneSummary({ byZone, isLoading }: ZoneSummaryProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Inventory by Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-10 bg-muted rounded animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!byZone || byZone.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Inventory by Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-4">
            No zones with inventory yet.
          </p>
          <div className="text-center">
            <Button variant="outline" size="sm" asChild>
              <Link to="/wms/locations">Setup Zones</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const maxQuantity = Math.max(...byZone.map((z) => z.total_quantity ?? 0));

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Inventory by Zone</CardTitle>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/wms/locations">Manage</Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {byZone.map((zone) => {
            const totalQty = zone.total_quantity ?? 0;
            const itemCount = zone.item_count ?? 0;
            const percentage = maxQuantity > 0 ? (totalQty / maxQuantity) * 100 : 0;
            return (
              <div key={zone.zone_id} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: zone.zone_color || '#6b7280' }}
                    />
                    <span className="font-medium">
                      {zone.zone_code} - {zone.zone_name}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="font-medium">{totalQty.toLocaleString()}</span>
                    <span className="text-muted-foreground ml-1 text-xs">
                      ({itemCount} items)
                    </span>
                  </div>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: zone.zone_color || '#6b7280',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
